import { useNavigate } from 'react-router-dom'
import { App, AppType } from '@flatfile/api-internal/api'
import { Button } from '@flatfile/baseline'
import { useCallback, useState } from 'react'
import { useCollection } from '../../resources/hooks/useCollection'
import { appConfigMap, getInstalledApp } from '../../utils/appsUtils'
import { useCreateResource, useUpdateResource } from '../../resources/hooks/useResourceMutation'
import getCollection from '../../resources/support/getCollection'
import CustomAppPath from '../../pages/CustomApps/CustomAppModalPath'
import { useEntitlements } from '../../hooks/useEntitlements'
import { useActiveEnvironment } from '../../hooks/useEnvironments'
import { useFilteredCollection } from '../../resources/hooks/useCollectionIds'
import { logger } from '../../utils/logging'
import { useCreateAutoBuildApp } from '../../hooks/useCreateAutoBuildApp'
import { useCreateFlatfileOSApp } from '../../hooks/useCreateFlatfileOSApp'

// Define the props type for the AppCTAs component
interface AppCTAsProps {
  appType: AppType
  learnMoreLink?: string
  onAnyAction?: () => void
  activatedAt?: string
  createdAt?: string
  customEntityPlural?: string
  appId?: string
  isAutobuild?: boolean
  isFlatfileOs?: boolean
}

export const getNavigationPath = (app: App) => {
  if (!app) {
    return '/'
  }
  const baseCustomPath = `/apps/custom/${app.id}`

  if (app.metadata?.autobuild) {
    if (app.metadata?.templateCreated) {
      return `${baseCustomPath}/build/template`
    }
    return `${baseCustomPath}/build/configure`
  }

  if (app.metadata?.flatfileos) {
    if (app.metadata?.templateCreated) {
      return `${baseCustomPath}/build/template`
    }
    return `${baseCustomPath}/flatfile-os/configure`
  }

  const appTypePath = app.type === AppType.Custom ? `custom/${app.id}` : app.namespace
  return `/apps/${appTypePath}/start/`
}

export const AppCTAs: React.FC<AppCTAsProps> = ({
  appType,
  onAnyAction,
  createdAt,
  appId,
  isAutobuild,
  isFlatfileOs,
}) => {
  const { environment, setActiveEnvironment } = useActiveEnvironment()
  const nonProdPredicate = useCallback((env: any) => env.isProd === false, [])
  const nonProdEnvIds = useFilteredCollection('ENVIRONMENTS', nonProdPredicate)
  const nav = useNavigate()
  const apps = useCollection('APPS')
  const app = getInstalledApp(apps, appType, appId)
  const [createApp] = useCreateResource('APPS')
  const [updateAccount] = useUpdateResource('ACCOUNTS')
  const { namespace } = appConfigMap[appType]
  const [loadingApp, setLoadingApp] = useState(false)
  const { createAutoBuildApp, isLoading: isCreatingAutoBuildApp } = useCreateAutoBuildApp()
  const { createFlatfileOSApp, isLoading: isCreatingFlatfileOSApp } = useCreateFlatfileOSApp()

  const { hasEntitlement } = useEntitlements()
  const projectsFlag = hasEntitlement('projectsEnabled')

  const handleInstall = async () => {
    try {
      const newApp = await createApp(appConfigMap[appType])
      if (!newApp) {
        throw new Error('Failed to create the app.')
      }
      if (apps.length === 0 && newApp.type !== AppType.Mapping) {
        await updateAccount({ defaultAppId: newApp.id })
      }
      getCollection('APPS').append(newApp)
      nav(`/apps/${namespace}/start/`)
    } catch (error) {
      logger.error('Failed to delete resource', { error })
      alert('Failed to install the app. Please try again later.')
    }
  }

  if (appType === AppType.Custom && isAutobuild) {
    return (
      <Button
        size='md'
        variant='outline'
        icon='arrow-right'
        iconPlacement='right'
        loading={loadingApp || isCreatingAutoBuildApp}
        onClick={async () => {
          setLoadingApp(true)
          try {
            // Use the shared hook to create an autobuild app
            const { appId, error } = await createAutoBuildApp()

            if (error || !appId) {
              logger.error('Failed to create autobuild app', { error })
              return
            }

            // Navigate to autobuild flow with the new app ID
            nav(`/apps/custom/${appId}/build/configure`)
          } catch (error) {
            logger.error('Failed to create app', { error })
          } finally {
            setLoadingApp(false)
          }
        }}
      >
        Build with Autobuild
      </Button>
    )
  }

  if (appType === AppType.Custom && isFlatfileOs) {
    return (
      <Button
        size='md'
        variant='outline'
        icon='arrow-right'
        iconPlacement='right'
        loading={loadingApp || isCreatingFlatfileOSApp}
        onClick={async () => {
          setLoadingApp(true)
          try {
            // Use the Flatfile OS hook to create a custom app
            const { appId, error } = await createFlatfileOSApp()

            if (error || !appId) {
              logger.error('Failed to create Flatfile OS app', { error })
              return
            }

            // Navigate to Flatfile OS flow with the new app ID
            nav(`/apps/custom/${appId}/flatfile-os/configure`)
          } catch (error) {
            logger.error('Failed to create app', { error })
          } finally {
            setLoadingApp(false)
          }
        }}
      >
        Build with Flatfile OS
      </Button>
    )
  }

  if (appType === AppType.Custom && !createdAt) {
    return <CustomAppPath mini={false} title='Get started' onAnyAction={onAnyAction} />
  }

  // Special case for Workbooks - in progress
  if (appType === AppType.Workbooks) {
    return (
      <Button
        size='md'
        variant='outline'
        icon='arrow-right'
        iconPlacement='right'
        onClick={() => {
          onAnyAction?.()
          nav(`/apps/workbooks/learn/`)
        }}
      >
        Learn more
      </Button>
    )
  }

  return (
    <>
      {app?.activatedAt ? (
        <div>
          <Button
            size='md'
            variant='outline'
            icon='arrow-right'
            iconPlacement='right'
            className='py-2'
            onClick={() => {
              !app?.environmentFilters.type.includes('PROD') &&
                environment?.isProd &&
                setActiveEnvironment(nonProdEnvIds[0])
              nav(`/apps/${app?.type === AppType.Custom ? `custom/${appId}` : namespace}/list/recent/`)
            }}
          >
            View {app.entityPlural ?? 'Spaces'}
          </Button>
        </div>
      ) : app ? (
        <div>
          <Button
            size='md'
            variant='outline'
            icon='arrow-right'
            iconPlacement='right'
            onClick={() => {
              onAnyAction?.()
              !app?.environmentFilters.type.includes('PROD') &&
                environment?.isProd &&
                setActiveEnvironment(nonProdEnvIds[0])
              nav(getNavigationPath(app))
            }}
          >
            Configure
          </Button>
        </div>
      ) : (
        <Button
          size='md'
          variant='outline'
          icon='arrow-right'
          iconPlacement='right'
          onClick={() => {
            onAnyAction?.()
            appType === AppType.Projects && !projectsFlag ? nav(`/apps/${namespace}/learn/`) : handleInstall()
          }}
        >
          {appType === AppType.Projects && !projectsFlag
            ? 'Request App'
            : appType === AppType.Custom
              ? 'Get started'
              : 'Install app'}
        </Button>
      )}
    </>
  )
}
